/*
 * Blooms & Beans — gift finder (sections/bnb-finder.liquid).
 * Guided 3-step form; choices are kept when moving back and forth. Results are
 * fetched from the matching collection with Shopify's native price filter and
 * rendered server-side by sections/bnb-finder-results.liquid (max 6 products).
 * No-result states explain which choice to relax; the budget is never changed
 * silently.
 */
const T = (() => { try { return JSON.parse(document.querySelector('[data-bnb-finder-i18n]')?.textContent || '{}'); } catch { return {}; } })();

function init(panel) {
  if (panel.dataset.ready) return;
  panel.dataset.ready = '1';
  const form = panel.querySelector('[data-form]');
  const steps = [...form.querySelectorAll('[data-step]')];
  const back = form.querySelector('[data-back]');
  const next = form.querySelector('[data-next]');
  const counter = form.querySelector('[data-stepcount]');
  const bars = [...form.querySelectorAll('.bnb-progress li')];
  const summary = form.querySelector('[data-summary]');
  const err = form.querySelector('[data-error]');
  const results = panel.querySelector('[data-results]');
  const names = ['occasion', 'type', 'budget'];
  let step = 0, controller = null;

  const checked = (name) => form.querySelector(`input[name="${name}"]:checked`);
  const labelOf = (input) => input?.closest('label')?.querySelector('span')?.textContent.trim() || '';

  function show(i, focus = true) {
    step = i;
    steps.forEach((s, k) => { s.hidden = k !== i; });
    bars.forEach((b, k) => { b.className = k < i ? 'is-done' : k === i ? 'is-current' : ''; });
    counter.textContent = (T.step || 'Step %c of %t').replace('%c', i + 1).replace('%t', steps.length);
    back.hidden = i === 0;
    next.textContent = i === steps.length - 1 ? (T.show || 'Show matches') : (T.next || 'Next');
    err.textContent = '';
    renderSummary();
    if (focus) (checked(names[i]) || steps[i].querySelector('input'))?.focus();
  }

  function renderSummary() {
    const items = names.slice(0, step).map((n, k) => ({ k, label: labelOf(checked(n)) })).filter((x) => x.label);
    summary.hidden = !items.length;
    summary.innerHTML = '';
    items.forEach(({ k, label }) => {
      const li = document.createElement('li');
      li.textContent = label;
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = T.edit || 'Change';
      b.setAttribute('aria-label', `${T.edit || 'Change'}: ${label}`);
      b.addEventListener('click', () => { results.hidden = true; show(k); });
      li.appendChild(b);
      summary.appendChild(li);
    });
  }

  back.addEventListener('click', () => show(Math.max(0, step - 1)));
  // A pointer click on an option moves on (even if it was already selected).
  // Keyboard users move with Next, so arrow keys never skip a step.
  form.addEventListener('click', (e) => {
    const label = e.target.closest('label.bnb-choice');
    if (!label || e.target.tagName === 'INPUT' || e.detail === 0) return;
    const input = label.querySelector('input');
    if (!input || input.name !== names[step] || step >= steps.length - 1) return;
    setTimeout(() => { if (input.checked) show(step + 1); }, 0);
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!checked(names[step])) { err.textContent = T.pickOne || 'Choose one option to continue.'; steps[step].querySelector('input')?.focus(); return; }
    if (step < steps.length - 1) { show(step + 1); return; }
    run();
  });

  function query() {
    const occ = checked('occasion'), type = checked('type').value, [min, max] = checked('budget').value.split('-').map(Number);
    const base = type === 'cakes' ? panel.dataset.cakesUrl : type === 'balloons' ? panel.dataset.balloonsUrl : (occ?.dataset.url || panel.dataset.allUrl);
    return { base, type, min, max, occLabel: labelOf(occ), typeLabel: labelOf(checked('type')), budgetLabel: labelOf(checked('budget')) };
  }

  function url(base, min, max, sectionId = true) {
    const p = new URLSearchParams();
    if (sectionId) p.set('section_id', 'bnb-finder-results');
    if (min > 0) p.set('filter.v.price.gte', String(min));
    if (max > 0) p.set('filter.v.price.lte', String(max));
    const q = p.toString();
    return base + (q ? `?${q}` : '');
  }

  async function fetchResults(base, min, max) {
    controller?.abort();
    controller = new AbortController();
    const r = await fetch(url(base, min, max), { signal: controller.signal, headers: { Accept: 'text/html' } });
    if (!r.ok) throw new Error(String(r.status));
    const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
    const wrap = doc.querySelector('[data-finder-results]');
    if (!wrap) throw new Error('parse');
    // Defensive: keep only cards whose live price is inside the chosen band.
    [...wrap.querySelectorAll('li[data-price]')].forEach((li) => {
      const cents = Number(li.dataset.price);
      if ((min > 0 && cents < min * 100) || (max > 0 && cents > max * 100)) li.remove();
    });
    return { wrap, count: Number(wrap.dataset.count) || 0, shown: wrap.querySelectorAll('li[data-price]').length };
  }

  async function run() {
    const q = query();
    results.hidden = false;
    results.innerHTML = `<p class="bnb-status" role="status">${T.loading || 'Finding matches…'}</p><ul class="bnb-grid" aria-hidden="true">${'<li><div class="bnb-skeleton"></div></li>'.repeat(3)}</ul>`;
    next.disabled = true;
    try {
      const res = await fetchResults(q.base, q.min, q.max);
      if (res.shown) renderHits(q, res);
      else await renderEmpty(q);
    } catch (e) {
      if (e.name === 'AbortError') return;
      results.innerHTML = '';
      const p = document.createElement('p'); p.className = 'bnb-status bnb-status--error'; p.setAttribute('role', 'alert'); p.textContent = T.error || "We couldn't load matches just now.";
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'bnb-btn bnb-btn--secondary bnb-btn--small'; retry.textContent = T.retry || 'Try again';
      retry.addEventListener('click', run);
      const link = document.createElement('a'); link.className = 'bnb-link'; link.href = url(q.base, q.min, q.max, false); link.textContent = T.seeAll || 'See all matches';
      const nav = document.createElement('div'); nav.className = 'bnb-stepnav'; nav.append(retry, link);
      results.append(p, nav);
    } finally {
      next.disabled = false;
    }
  }

  function heading(q, count) {
    const h = document.createElement('h3');
    h.className = 'bnb-h3';
    h.textContent = `${T.resultsHeading || 'Your matches'} (${count})`;
    const sub = document.createElement('p');
    sub.className = 'bnb-finder__note';
    sub.textContent = [q.type === 'flowers' ? q.occLabel : null, q.typeLabel, q.budgetLabel].filter(Boolean).join(' · ');
    return [h, sub];
  }

  function renderHits(q, res) {
    results.innerHTML = '';
    const head = document.createElement('div'); head.className = 'bnb-head'; head.style.marginBottom = '16px';
    const left = document.createElement('div'); left.append(...heading(q, res.count));
    const all = document.createElement('a'); all.className = 'bnb-link'; all.href = url(q.base, q.min, q.max, false); all.textContent = `${T.seeAll || 'See all matches'} (${res.count})`;
    head.append(left, all);
    results.append(head, res.wrap);
    const note = document.createElement('p'); note.className = 'bnb-finder__note';
    note.textContent = [q.type !== 'flowers' ? T.cakesNote : null, T.scopeNote].filter(Boolean).join(' ');
    results.append(note);
    if (T.builderLink && document.getElementById('gift-builder')) {
      const b = document.createElement('a'); b.className = 'bnb-link'; b.href = '#gift-builder'; b.textContent = T.builderLink; results.append(b);
    }
    results.focus({ preventScroll: false });
  }

  async function renderEmpty(q) {
    results.innerHTML = '';
    const box = document.createElement('div'); box.className = 'bnb-finder__empty';
    const p = document.createElement('p'); p.textContent = `${T.noResults || 'Nothing matches all three choices.'} ${[q.type === 'flowers' ? q.occLabel : null, q.typeLabel, q.budgetLabel].filter(Boolean).join(' · ')}`;
    box.append(p);
    const nav = document.createElement('div'); nav.className = 'bnb-stepnav';
    // Offer clearly labelled alternatives; never change the budget silently.
    if (q.min > 0 || q.max > 0) {
      try {
        const any = await fetchResults(q.base, 0, 0);
        if (any.count) {
          const b = document.createElement('button'); b.type = 'button'; b.className = 'bnb-btn bnb-btn--secondary bnb-btn--small';
          b.textContent = `${T.relaxBudget || 'Show any budget'} (${any.count})`;
          b.addEventListener('click', () => { form.querySelector('input[name="budget"][value="0-0"]').checked = true; renderSummary(); renderHits({ ...q, min: 0, max: 0, budgetLabel: labelOf(checked('budget')) }, any); });
          nav.append(b);
        }
      } catch {}
    }
    const change = document.createElement('button'); change.type = 'button'; change.className = 'bnb-btn bnb-btn--secondary bnb-btn--small';
    change.textContent = T.edit || 'Change'; change.addEventListener('click', () => { results.hidden = true; show(0); });
    nav.append(change);
    box.append(nav);
    results.append(box);
    results.focus({ preventScroll: false });
  }

  show(0, false);
}

document.querySelectorAll('[data-bnb-finder]').forEach(init);
document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-bnb-finder]').forEach(init));
